"use client"

import { useState, useEffect, useRef } from "react"
import axios from "axios"
import ConfirmationDialog from "../components/ConfirmationDialog"

const ProductList = () => {
  const [selectedItems, setSelectedItems] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("All")
  const [sortBy, setSortBy] = useState("Last 7 Days")
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [products, setProducts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [productToDelete, setProductToDelete] = useState(null)
  const [isImporting, setIsImporting] = useState(false)
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 })
  const [importErrors, setImportErrors] = useState([])
  const fileInputRef = useRef(null)

  useEffect(() => {
    fetchProducts()
  }, [])

  const fetchProducts = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/get-products`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )
      if (response.data && response.data.data) {
        setProducts(response.data.data)
      }
    } catch (err) {
      setError("Failed to fetch products")
      console.error("Error fetching products:", err)
    } finally {
      setIsLoading(false)
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(products.map((product) => product.id))
    } else {
      setSelectedItems([])
    }
  }

  const handleSelectItem = (id) => {
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
  }

  const filteredProducts = products.filter(
    (product) =>
      product.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.product_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.category_name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleEditClick = (product) => {
    setEditingProduct(product)
    setIsEditDialogOpen(true)
  }

  const handleEditProduct = async (e) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)
      setError(null)
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URI}/api/edit-product/${editingProduct.id}`,
        {
          product_code: editingProduct.product_code,
          product_name: editingProduct.product_name,
          category_name: editingProduct.category_name,
          product_unit: editingProduct.product_unit,
          product_description: editingProduct.product_description
        },
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data) {
        // Refresh the products list after successful update
        await fetchProducts()
        setIsEditDialogOpen(false)
        setEditingProduct(null)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update product")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteClick = (product) => {
    setProductToDelete(product)
    setIsDeleteDialogOpen(true)
  }

  const handleDeleteProduct = async () => {
    try {
      setIsSubmitting(true)
      setError(null)
      
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URI}/api/delete-product/${productToDelete.id}`,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.data) {
        // Refresh the products list after successful deletion
        await fetchProducts()
        setIsDeleteDialogOpen(false)
        setProductToDelete(null)
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete product")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFileSelect = (event) => {
    const file = event.target.files[0]
    if (file) {
      const formData = new FormData()
      formData.append('file', file)

      setIsImporting(true)
      setImportProgress({ current: 0, total: 1, success: 0, failed: 0 })
      setImportErrors([])

      axios.post(
        `${process.env.REACT_APP_API_URI}/api/import-products`,
        formData,
        {
          headers: {
            'authorization': localStorage.getItem("accessToken"),
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setImportProgress(prev => ({
              ...prev,
              current: percentCompleted
            }))
          }
        }
      )
      .then(response => {
        if (response.data) {
          setImportProgress(prev => ({
            ...prev,
            success: 1
          }))
          // Refresh the products list after successful import
          fetchProducts()
        }
      })
      .catch(err => {
        setImportProgress(prev => ({
          ...prev,
          failed: 1
        }))
        setImportErrors([{
          error: err.response?.data?.message || "Failed to import products"
        }])
      })
      .finally(() => {
        setIsImporting(false)
      })
    }
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">Products</h1>
            <p className="text-gray-500">Manage your products</p>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={() => window.location.href = '/add-product'}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Product
            </button>
            <button 
              onClick={handleImportClick}
              disabled={isImporting}
              className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Import Products
            </button>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".csv"
              className="hidden"
            />
          </div>
        </div>

        {/* Import Progress */}
        {isImporting && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Importing Products</h3>
            <div className="space-y-4">
              <div className="flex justify-between text-sm text-gray-600">
                <span>Progress: {importProgress.current}%</span>
                <span>Success: {importProgress.success} | Failed: {importProgress.failed}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-orange-500 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${importProgress.current}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        {/* Import Errors */}
        {importErrors.length > 0 && (
          <div className="mb-6 bg-white rounded-lg shadow-sm border border-red-200 p-6">
            <h3 className="text-lg font-medium text-red-900 mb-4">Import Errors</h3>
            <div className="space-y-2">
              {importErrors.map((error, index) => (
                <div key={index} className="text-sm text-red-600">
                  {error.error}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex justify-between items-center mb-6">
            {/* Search */}
            <div className="relative">
              <svg
                className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none w-64"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Status</span>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                >
                  <option>All</option>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
                <svg
                  className="w-4 h-4 text-gray-400 -ml-8 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Sort By</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                >
                  <option>Last 7 Days</option>
                  <option>Last 30 Days</option>
                  <option>Last 90 Days</option>
                </select>
                <svg
                  className="w-4 h-4 text-gray-400 -ml-8 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            {isLoading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 w-12">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === products.length}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                      />
                    </th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Product Code</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Product Name</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Category</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Unit</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Created On</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Added By</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product) => (
                    <tr key={product.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(product.id)}
                          onChange={() => handleSelectItem(product.id)}
                          className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                        />
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-900">{product.product_code}</td>
                      <td className="py-4 px-4 text-sm text-gray-900">{product.product_name}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{product.category_name}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{product.product_unit}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{formatDate(product.created_at)}</td>
                      <td className="py-4 px-4 text-sm text-gray-600">{product.added_by}</td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => handleEditClick(product)}
                            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button 
                            onClick={() => handleDeleteClick(product)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Row Per Page</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => setRowsPerPage(Number(e.target.value))}
                  className="border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <svg
                  className="w-3 h-3 text-gray-400 -ml-6 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              <span className="text-sm text-gray-600">Entries</span>
            </div>

            <div className="flex items-center gap-2">
              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button className="w-8 h-8 rounded bg-orange-500 text-white text-sm font-medium">1</button>

              <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Product Dialog */}
      {isEditDialogOpen && editingProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md relative">
            <button 
              className="absolute top-2 right-2 text-gray-400 hover:text-gray-700"
              onClick={() => {
                setIsEditDialogOpen(false)
                setEditingProduct(null)
              }}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h2 className="text-lg font-semibold mb-4">Edit Product</h2>
            <form onSubmit={handleEditProduct} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
              <div className="space-y-1">
                <label htmlFor="edit-code" className="block text-sm font-medium text-gray-700">Product Code</label>
                <input
                  type="text"
                  id="edit-code"
                  value={editingProduct.product_code}
                  onChange={(e) => setEditingProduct(prev => ({ ...prev, product_code: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  placeholder="Enter product code"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="edit-name" className="block text-sm font-medium text-gray-700">Product Name</label>
                <input
                  type="text"
                  id="edit-name"
                  value={editingProduct.product_name}
                  onChange={(e) => setEditingProduct(prev => ({ ...prev, product_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  placeholder="Enter product name"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="edit-category" className="block text-sm font-medium text-gray-700">Category</label>
                <input
                  type="text"
                  id="edit-category"
                  value={editingProduct.category_name}
                  onChange={(e) => setEditingProduct(prev => ({ ...prev, category_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  placeholder="Enter category name"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="edit-unit" className="block text-sm font-medium text-gray-700">Unit</label>
                <input
                  type="text"
                  id="edit-unit"
                  value={editingProduct.product_unit}
                  onChange={(e) => setEditingProduct(prev => ({ ...prev, product_unit: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  placeholder="Enter product unit"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="edit-description" className="block text-sm font-medium text-gray-700">Description</label>
                <textarea
                  id="edit-description"
                  value={editingProduct.product_description}
                  onChange={(e) => setEditingProduct(prev => ({ ...prev, product_description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
                  placeholder="Enter product description"
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditDialogOpen(false)
                    setEditingProduct(null)
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Updating..." : "Update Product"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setProductToDelete(null)
        }}
        onConfirm={handleDeleteProduct}
        title="Delete Product"
        message={`Are you sure you want to delete the product "${productToDelete?.product_name}"? This action cannot be undone.`}
        confirmText="Delete"
        type="danger"
      />
    </div>
  )
}

export default ProductList 